<template>
  <div v-rect-loading="loading" class="open-api-config-editor">
    <div class="title">
      {{ openApi && $t(`${i18nPrefix}.openApiType.${openApi.type}`) }} {{ $t(`${i18nPrefix}.title.configRequest`) }}
    </div>
    <div class="editor-body">
      <div class="request-config config-active">
        <div class="config-title">{{ $t(`${i18nPrefix}.label.isActive`) }}</div>
        <div class="config-editor">
          <el-switch v-model="config.is_active"></el-switch>
        </div>
      </div>
      <div class="request-config config-auth">
        <div class="config-title">{{ $t(`${i18nPrefix}.label.isRequireAuth`) }}</div>
        <div class="config-editor">
          <el-switch v-model="config.require_auth"></el-switch>
        </div>
      </div>
      <div class="request-config config-name">
        <div class="config-title">{{ $t(`${i18nPrefix}.label.name`) }}</div>
        <div class="config-editor">
          <el-input v-model="config.custom_name"></el-input>
        </div>
      </div>
      <div ref="customPath" class="request-config config-path" :class="error.custom_path && 'has-error'">
        <div class="config-title">{{ $t(`${i18nPrefix}.label.request.customPath`) }}</div>
        <div class="config-editor">
          <el-button class="random-btn" type="primary" @click="onRandomBtnClick">{{ $t(`${i18nPrefix}.label.randomGeneration`) }}</el-button>
          <span v-auto-tooltip="fullRequestPath" class="full-path">{{ fullRequestPath }}</span>
          <div
            v-clipboard:copy="fullRequestPath"
            v-clipboard:success="handleCopySuccess"
            v-clipboard:error="handleCopyError"
            v-auto-tooltip.always="$t('common.btn.copy')"
            class="copy-btn"
          >
            <i class="btn-icon el-icon-document-copy"></i>
          </div>
          <!-- <el-input class="path-input" v-model="config.custom_path" @input="onCustomPathInput">
            <template v-if="pathPrefix" #prepend>{{ pathPrefix }}</template>
            <template #append>
              <el-button class="random-btn" @click="onRandomBtnClick">{{ $t(`${i18nPrefix}.label.randomGeneration`) }}</el-button>
            </template>
          </el-input>
          <div class="path-preview">
            <span v-auto-tooltip="fullRequestPath" class="full-path">{{ $t(`${i18nPrefix}.label.request.fullPath`, { path: fullRequestPath }) }}</span>
            <div
              v-clipboard:copy="fullRequestPath"
              v-clipboard:success="handleCopySuccess"
              v-clipboard:error="handleCopyError"
              class="copy-btn"
            >
              <i class="btn-icon el-icon-document-copy"></i>
            </div>
          </div> -->
        </div>
      </div>
      <TableList
        class="request-config config-param"
        row-key="_vid"
        no-action
        :titleI18nKey="`${i18nPrefix}.label.request.customParams`"
        :list-data="paramInfoList"
        :table-columns="tableColumns"
        :show-create-btn="false"
      >
        <template #valueEditor="{ row }">
          <el-select
            v-if="row.range"
            v-model="customParams[row.key]"
            filterable
            :ref="`params_${row.key}`"
            :class="error.params[row.key] && 'has-error'"
            :clearable="!row.required"
            :multiple="row.multiple"
            @change="validateCustomParam(row)"
          >
            <el-option
              v-for="(item, index) in row.range"
              :key="index"
              :label="item.label"
              :value="item.value"
              :style="item.style"
            ></el-option>
          </el-select>
          <el-input
            v-else
            v-model="customParams[row.key]"
            :ref="`params_${row.key}`"
            :class="error.params[row.key] && 'has-error'"
            @input="validateCustomParam(row)"
          ></el-input>
        </template>
      </TableList>
    </div>
    <div class="editor-foot">
      <el-button class="cancel-btn" @click="onCancel">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="save-btn" @click="onSave">{{ $t('common.btn.save') }}</el-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { getI18nPropertyValue } from '@/i18n'
import { OPEN_API_BASE_PATH } from '@/constant/openApi'

const CODES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789abcdefghijklmnopqrstuvwxyz'

export default {
  components: {
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.openApi',
      loading: false,
      vid: 0,
      openApi: null,
      pathPrefix: null,
      paramInfoList: [],
      error: {
        custom_path: false,
        params: {}
      },
      config: {
        id: null,
        custom_name: null,
        custom_path: null,
        is_active: true,
        require_auth: true,
        request_config: {
          params: {}
        }
      }
    }
  },
  computed: {
    openApiId () {
      return this.$route.params.openApiId
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    fullRequestPath () {
      let path = this.config.custom_path
      if (path && !path.endsWith('/')) {
        path += '/'
      } else if (!path) {
        path = ''
      }
      return `${window.location.origin}${OPEN_API_BASE_PATH}${this.pathPrefix || '/'}${path}`
    },
    tableColumns () {
      const columns = [
        {
          labelI18nKey: `${this.i18nPrefix}.label.request.param`,
          dataKey: 'key'
        },
        {
          labelI18nKey: `${this.i18nPrefix}.label.request.isRequired`,
          dataKey: 'required',
          formatter: (data) => this.$t(`common.label.${data ? 'yes' : 'no'}`)
        },
        {
          labelI18nKey: `${this.i18nPrefix}.label.request.defaultValue`,
          dataKey: 'default',
          formatter: (data) => (data === null || data === undefined) ? '-' : data
        },
        {
          labelI18nKey: `${this.i18nPrefix}.label.request.fixedValue`,
          dataKey: 'value',
          slotName: 'valueEditor'
        }
      ]
      return columns
    },
    customParams () {
      return this.config.request_config.params
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('openApi', ['getOpenApiConfig', 'getOpenApiConfigById', 'saveOpenApiConfig', 'updateOpenApiConfig']),
    async initData () {
      this.loading = true
      try {
        const params = { openApiId: this.openApiId }
        let func
        if (this.isEditMode) {
          func = this.getOpenApiConfigById
          params.configId = this.$route.params.configId
        } else {
          func = this.getOpenApiConfig
          this.onRandomBtnClick()
        }
        const resp = await func(params)
        if (resp) {
          this.openApi = resp.open_api
          this.pathPrefix = resp.custom_path_prefix
          if (this.isEditMode) {
            const config = resp.config
            this.config = config
          }
          this.paramInfoList = _.map(resp.request.param_info_list, (paramInfo, index) => {
            paramInfo._vid = index + 1
            if (paramInfo.range) {
              paramInfo.range = _.map(paramInfo.range, rangeItem => {
                const info = {
                  label: rangeItem,
                  value: paramInfo.range_value_key ? rangeItem[paramInfo.range_value_key] : rangeItem
                }
                // 保存后的 参数值类型都是 字符串，将这里的 value 转换为字符串，防止 el-select 无法回显正确的选项
                info.value += ''
                if (paramInfo.range_data_type === 'company') {
                  info.style = { paddingLeft: `${(rangeItem.level || 1) * 15}px` }
                  Object.defineProperty(info, 'label', {
                    configurable: true,
                    enumerable: true,
                    get () {
                      const i18nName = getI18nPropertyValue(rangeItem, 'cn_name', 'name')
                      return `${rangeItem.id} - ${i18nName}`
                    }
                  })
                }
                return info
              })
            }
            return paramInfo
          })
        }
      } finally {
        this.loading = false
      }
    },
    onRandomBtnClick () {
      const len = 30
      const maxIndex = CODES.length - 1
      let str = ''
      _.times(len, () => {
        str += CODES[_.random(maxIndex)]
      })
      this.config.custom_path = str
      this.onCustomPathInput()
    },
    onCustomPathInput () {
      this.error.custom_path = !this.config.custom_path
    },
    validateCustomParam (paramInfo) {
      let hasError = false
      if (paramInfo.required && [null, undefined].includes(paramInfo.default)) {
        hasError = Boolean([null, undefined].includes(this.customParams[paramInfo.key]))
      }
      this.$set(this.error.params, paramInfo.key, hasError)
    },
    onCancel () {
      this.$router.back()
    },
    async onSave () {
      if (this.loading) {
        return
      }

      this.loading = true
      try {
        if (!this.config.custom_path) {
          const el = this.$refs.customPath
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          this.error.custom_path = true
          this.$message.error(this.$t('common.message.requiredFormField'))
          return
        }
        let success = true
        _.forEach(this.paramInfoList, info => {
          if (info.required && [null, undefined].includes(info.default)) {
            if ([null, undefined].includes(this.customParams[info.key])) {
              success = false
              this.$set(this.error.params, info.key, true)
              this.$message.error(this.$t(`${this.i18nPrefix}.message.needSetRequiredParam`))
              const el = this.$refs[`params_${info.key}`].$el
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
              return false
            }
          }
        })
        if (!success) {
          return
        }
        const info = {
          openApiId: this.openApi.id,
          config: this.config
        }
        let saveFunc
        if (this.isEditMode) {
          info.configId = this.config.id
          saveFunc = this.updateOpenApiConfig
        } else {
          saveFunc = this.saveOpenApiConfig
        }
        const resp = await saveFunc(info)
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.$nextTick(() => this.$router.push('/v-admin/company-admin/open-api/list'))
        } else {
          this.$message.success(this.$t('common.message.saveFail'))
        }
      } catch (err) {
        if (err && err.code === 10001003 && err.message?.includes('custom_path')) {
          const el = this.$refs.customPath
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          this.error.custom_path = true
          this.$message.error(this.$t(`${this.i18nPrefix}.message.duplicatedCustomPath`))
        } else {
          throw err
        }
      } finally {
        this.loading = false
      }
    },
    handleCopySuccess () {
      this.$message.success(this.$t('common.message.actionSuccess', { action: this.$t('common.btn.copy') }))
    },
    handleCopyError () {
      this.$message.error(this.$t('common.message.actionFail', { action: this.$t('common.btn.copy') }))
    }
  }
}
</script>

<style lang="scss" scoped>
.open-api-config-editor {
  .title {
    margin-left: 0;
    margin-right: auto;
    color: #303133;
    line-height: 25px;
    font-size: 18px;
    font-weight: 500;
  }

  .editor-body {
    margin-top: 20px;

    .request-config {
      display: flex;
      margin-top: 20px;
      line-height: 32px;

      .config-title {
        width: 100px;
        flex-shrink: 0;
        // line-height: 32px;
      }

      .config-editor {
        flex: 1;
        max-width: calc(100% - 100px);
        padding-left: 20px;
      }

      &.config-auth {
        margin-top: 10px;
      }

      &.config-path {
        .config-editor {
          display: flex;

          .random-btn {
            padding: 0 20px;
          }

          .full-path {
            margin-left: 10px;
          }

          .copy-btn {
            margin-left: 10px;
            cursor: pointer;
            color: #0096cc;
            font-size: 16px;
          }
        }
        // .path-input {
        //   ::v-deep {
        //     .el-input-group__append {
        //       background: #0096cc;
        //       color: #ffffff;
        //     }
        //   }
        // }

        // .path-preview {
        //   display: flex;

        //   .copy-btn {
        //     margin-left: 10px;
        //     cursor: pointer;
        //     color: #0096cc;
        //     font-size: 16px;
        //   }
        // }
      }

      &.config-param {
        display: block;
        margin-top: 10px;
      }
    }

    .has-error {
      ::v-deep {
        .el-input__inner {
          border-color: #f56c6c;
        }
      }
    }
  }
  .editor-foot {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &.save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
